import { useState, useEffect, useMemo } from "react";
import api from "../services/api";
import jalaali from "jalaali-js";

export default function Reports() {
  const [loading, setLoading] = useState(true);
  
  const [workLogs, setWorkLogs] = useState([]);
  const [requests, setRequests] = useState([]);

  // Filter settings
  const [currentDate] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState(jalaali.toJalaali(new Date()).jm);
  const [selectedYear, setSelectedYear] = useState(jalaali.toJalaali(new Date()).jy);

  // Modal settings
  const [activeModal, setActiveModal] = useState(null); 
  const [modalData, setModalData] = useState([]); 
  const [modalTitle, setModalTitle] = useState(""); 
  const [modalType, setModalType] = useState(""); 
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [logsRes, reqRes] = await Promise.all([
            api.get("work-logs/"),
            api.get("requests/"),
        ]);
        setWorkLogs(logsRes.data);
        setRequests(reqRes.data);
      } catch (error) {
        console.error("Error fetching report data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // --- Helper Functions ---
  const toPersianDigits = (num) => {
    if (num === undefined || num === null || isNaN(num)) return "۰";
    return Number(num).toLocaleString("fa-IR", { useGrouping: false });
  };

  const formatJalaliDate = (isoDate) => {
      if(!isoDate) return "-";
      const d = new Date(isoDate);
      if (isNaN(d.getTime())) return "-";
      const j = jalaali.toJalaali(d);
      return `${toPersianDigits(j.jy)}/${toPersianDigits(String(j.jm).padStart(2,'0'))}/${toPersianDigits(String(j.jd).padStart(2,'0'))}`;
  };

  const getEfficiencyColor = (percent) => {
      if (percent > 0) return '#10b981'; 
      if (percent < 0) return '#ef4444'; 
      return '#6b7280'; 
  };

  // --- Calculations ---
  const last6Months = useMemo(() => {
      const months = [];
      const persianMonths = ["فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور", "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"];
      let m = jalaali.toJalaali(currentDate).jm;
      let y = jalaali.toJalaali(currentDate).jy;

      for (let i = 0; i < 6; i++) {
          months.push({ id: m, year: y, name: persianMonths[m - 1] });
          m--;
          if (m < 1) { m = 12; y--; }
      }
      return months;
  }, [currentDate]);

  const filteredLogs = useMemo(() => {
      return workLogs.filter(log => {
          if (!log.start_time) return false;
          const d = new Date(log.start_time);
          if (isNaN(d.getTime())) return false;
          const j = jalaali.toJalaali(d);
          return j.jm === selectedMonth && j.jy === selectedYear;
      });
  }, [workLogs, selectedMonth, selectedYear]);

  const stats = useMemo(() => {
      const projects = {};
      const activities = {};
      
      let totalDoneCount = 0;
      let totalDoneEstimated = 0; // فقط برای نمایش ساعت کل نگه می‌داریم
      
      // متغیر جدید برای جمع درصدها
      let totalEfficiencySum = 0;

      filteredLogs.forEach(log => {
          const isDone = log.status === 'done';
          
          const estimatedMin = log.estimated_minutes || 0;
          // دریافت درصد محاسبه شده از بک‌اند
          const effPercent = log.efficiency_percentage || 0;

          if (isDone) {
              totalDoneEstimated += estimatedMin;
              totalDoneCount++;
              // جمع زدن درصدها برای میانگین ساده
              totalEfficiencySum += effPercent;
          }

          // Project
          const pName = log.project_title || "بدون پروژه";
          if (!projects[pName]) projects[pName] = { 
              title: pName, count: 0, sumEfficiency: 0, logs: [] 
          };
          projects[pName].logs.push(log);
          if (isDone) {
              projects[pName].sumEfficiency += effPercent;
              projects[pName].count++;
          }

          // Activity
          const aName = log.task_type_title || "سایر";
          if (!activities[aName]) activities[aName] = { 
              title: aName, count: 0, sumEfficiency: 0, logs: [] 
          };
          activities[aName].logs.push(log);
          if (isDone) {
              activities[aName].sumEfficiency += effPercent;
              activities[aName].count++;
          }
      });

      // محاسبه میانگین ساده کل ماه
      let totalEffPercent = 0;
      if (totalDoneCount > 0) {
          totalEffPercent = Math.round(totalEfficiencySum / totalDoneCount);
      }

      return {
          // مرتب‌سازی بر اساس تعداد کار انجام شده (یا هر منطق دیگری که دوست داری)
          projects: Object.values(projects).filter(p => p.count > 0).sort((a, b) => b.count - a.count), 
          activities: Object.values(activities).filter(a => a.count > 0).sort((a, b) => b.count - a.count),
          totalDoneCount,
          totalDoneEstimated,   
          totalEffPercent
      };
  }, [filteredLogs]);

  const rawChartData = useMemo(() => {
      return last6Months.map(month => {
          const monthLogs = workLogs.filter(log => {
              if(!log.start_time) return false;
              const d = new Date(log.start_time);
              if (isNaN(d.getTime())) return false;
              const j = jalaali.toJalaali(d);
              return j.jm === month.id && j.jy === month.year && log.status === 'done';
          });

          let sumEfficiency = 0;
          let count = 0;

          monthLogs.forEach(l => {
              sumEfficiency += (l.efficiency_percentage || 0);
              count++;
          });

          let percent = 0;
          if (count > 0) {
              percent = Math.round(sumEfficiency / count);
          }
          
          return { ...month, efficiencyPercent: percent };
      }).reverse();
  }, [workLogs, last6Months]);

  const chartScaleMax = useMemo(() => {
      const maxAbsPercent = Math.max(...rawChartData.map(d => Math.abs(d.efficiencyPercent)), 0);
      if (maxAbsPercent <= 100) return 100;
      return Math.ceil(maxAbsPercent / 10) * 10;
  }, [rawChartData]);


  // --- Modal Handlers ---
  const openDetailsModal = (title, logs, type) => {
      setModalTitle(title);
      setModalData(logs);
      setModalType(type); 
      setCurrentPage(1);
      setActiveModal('details');
  };

  const openRequestsModal = () => {
      setModalTitle("لیست درخواست‌ها");
      setModalData(requests);
      setActiveModal('requests');
  };

  const closeModal = () => {
      setActiveModal(null);
      setModalData([]);
  };

  const paginatedModalData = useMemo(() => {
      const start = (currentPage - 1) * itemsPerPage;
      return modalData.slice(start, start + itemsPerPage);
  }, [modalData, currentPage]);

  const totalPages = Math.ceil(modalData.length / itemsPerPage);


  if (loading) return <div className="report-main"><div className="container loading-container">در حال دریافت اطلاعات...</div></div>;

  const latestRequest = requests.length > 0 ? requests[0] : null;

  return (
    <>
    <div className="report-main">
        <div className="container">
            <div className="row p-full">
                
                {/* --- 1. Requests Section --- */}
                <div className="col-12">
                    <div className="filteredWorkLogs_request">
                        <h2>درخواست‌ها</h2>
                        <section onClick={openRequestsModal} style={{cursor:'pointer'}}>
                            <h3><svg><use xlinkHref="#taskIcon"></use></svg>ثبت شده</h3>
                            <span style={{fontWeight: 'normal'}}>{toPersianDigits(requests.length)} درخواست</span>
                        </section>
                        <section>
                            <h3>آخرین درخواست</h3>
                            <span style={{fontWeight: 'normal'}}>
                                {latestRequest ? (
                                    latestRequest.status === 'approved' ? <span style={{color:'#10b981'}}>تایید شد</span> :
                                    latestRequest.status === 'rejected' ? <span style={{color:'#ef4444'}}>رد شده</span> :
                                    <span style={{color:'#f59e0b'}}>در انتظار</span>
                                ) : "---"}
                            </span>
                            <p>{latestRequest?.description || "(بدون توضیح)"}</p>
                        </section>
                    </div>
                </div>

                {/* --- 2. نمودار بهره‌وری --- */}
                <div className="col-12">
                    <div className="filteredWorkLogs_wrapper">
                        <h2>میزان بهره‌وری (۶ ماه اخیر)</h2>
                        <div className="filteredWorkLogs_box">
                            {/* ستون‌های نمودار */}
                            <div>
                                {rawChartData.map((d, i) => {
                                    // محاسبه ارتفاع نسبی ستون
                                    const heightPercent = (Math.abs(d.efficiencyPercent) / chartScaleMax) * 100;
                                    
                                    // منطق تفکیک role-1 (مثبت) و role-2 (منفی)
                                    // اگر مثبت بود، role-1 پر شود و role-2 صفر
                                    // اگر منفی بود، role-1 صفر و role-2 پر شود
                                    const role1 = d.efficiencyPercent > 0 ? `${heightPercent}%` : "0%";
                                    const role2 = d.efficiencyPercent < 0 ? `${heightPercent}%` : "0%";

                                    return (
                                        <div key={i} className={d.id === selectedMonth ? "active" : ""}>
                                            <span 
                                                style={{ 
                                                    "--role-1": role1,
                                                    "--role-2": role2,
                                                }}
                                                aria-label={`${d.name} : ${d.efficiencyPercent > 0 ? "+" : ""}${toPersianDigits(d.efficiencyPercent)}%`}
                                            ></span>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* محور عمودی */}
                            <div>
                                <span>100%</span>
                                <span>80%</span>
                                <span>60%</span>
                                <span>40%</span>
                                <span>20%</span>
                            </div>

                            {/* محور افقی */}
                            <div>
                                {rawChartData.map((d, i) => {
                                    // محاسبه ارتفاع نسبی ستون
                                    const heightPercent = (Math.abs(d.efficiencyPercent) / chartScaleMax) * 100;
                                    
                                    // منطق تفکیک role-1 (مثبت) و role-2 (منفی)
                                    // اگر مثبت بود، role-1 پر شود و role-2 صفر
                                    // اگر منفی بود، role-1 صفر و role-2 پر شود
                                    const role1 = d.efficiencyPercent > 0 ? `${heightPercent}%` : "0%";
                                    const role2 = d.efficiencyPercent < 0 ? `${heightPercent}%` : "0%";

                                    return (
                                        <div key={i} className={d.id === selectedMonth ? "active" : ""}>
                                            <span 
                                                style={{ 
                                                    "--role-1": role1,
                                                    "--role-2": role2,
                                                }}
                                                aria-label={`${d.name} : ${d.efficiencyPercent > 0 ? "+" : ""}${toPersianDigits(d.efficiencyPercent)}%`}
                                            ></span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div>
                                <span>-20%</span>
                                <span>-40%</span>
                                <span>-60%</span>
                                <span>-80%</span>
                                <span>-100%</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- 3. Monthly Filter --- */}
                <div className="col-12">
                    <div className="filteredWorkLogs">
                        {last6Months.map(m => (
                            <button 
                                key={`${m.year}-${m.id}`} 
                                className={selectedMonth === m.id ? "active" : ""}
                                onClick={() => {
                                    setSelectedMonth(m.id);
                                    setSelectedYear(m.year);
                                }}
                            >
                                {m.name}
                            </button>
                        ))}
                        <span style={{width:'50px'}}></span>
                    </div>
                </div>

                {/* --- 3.5 Monthly Summary --- */}
                <div className="col-12">
                    <div className="filteredWorkLogs_request summary-box" style={{gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px'}}>
                        <section>
                            <h3>کارهای انجام شده</h3>
                            <span>{toPersianDigits(stats.totalDoneCount)}</span>
                        </section>
                        <section>
                            <h3>زمان تخمینی کل</h3>
                            <span>{toPersianDigits(Math.floor(stats.totalDoneEstimated / 60))} ساعت</span>
                        </section>
                        <section>
                            <h3>بازدهی کل ماه</h3>
                            <span style={{ color: getEfficiencyColor(stats.totalEffPercent), direction: 'ltr' }}>
                                {stats.totalEffPercent > 0 ? "+" : ""}{toPersianDigits(stats.totalEffPercent)}%
                            </span>
                        </section>
                    </div>
                </div>

                {/* --- 4. Projects Section --- */}
                <div className="col-12">
                    <div className="filteredWorkLogs_wrapper">
                        <h2>پروژه‌ها</h2>
                        <section>
                            <div>
                                <span>عنوان</span>
                                <span>تعداد (تمام شده)</span>
                                <span>بازدهی</span>
                                <span>جزئیات</span>
                            </div>
                            
                            {stats.projects.length > 0 ? stats.projects.map((proj, i) => {
                                // محاسبه میانگین ساده برای پروژه
                                let percent = 0;
                                if (proj.count > 0) {
                                    percent = Math.round(proj.sumEfficiency / proj.count);
                                }

                                return (
                                    <div key={i}>
                                        <span style={{fontWeight: 'normal'}}>{proj.title}</span>
                                        <span style={{fontWeight: 'normal'}}>{toPersianDigits(proj.count)}</span>
                                        <span style={{fontWeight: 'normal', direction:'ltr', color: getEfficiencyColor(percent)}}>
                                            {percent > 0 ? "+" : ""}{toPersianDigits(percent)}%
                                        </span>
                                        <button onClick={() => openDetailsModal(`پروژه: ${proj.title}`, proj.logs, 'project')}>بیشتر</button>
                                    </div>
                                );
                            }) : (
                                <div className="empty-state">داده‌ای برای این ماه وجود ندارد</div>
                            )}
                        </section>
                    </div>
                </div>

                {/* --- 5. Activities Section --- */}
                <div className="col-12">
                    <div className="filteredWorkLogs_wrapper">
                        <h2>فعالیت‌ها</h2>
                        <section>
                            <div>
                                <span>نوع</span>
                                <span>تعداد (تمام شده)</span>
                                <span>بازدهی</span>
                                <span>جزئیات</span>
                            </div>

                            {stats.activities.length > 0 ? stats.activities.map((act, i) => {
                                // محاسبه میانگین ساده برای فعالیت
                                let percent = 0;
                                if (act.count > 0) {
                                    percent = Math.round(act.sumEfficiency / act.count);
                                }

                                return (
                                    <div key={i}>
                                        <span style={{fontWeight: 'normal'}}>{act.title}</span>
                                        <span style={{fontWeight: 'normal'}}>{toPersianDigits(act.count)}</span>
                                        <span style={{fontWeight: 'normal', direction:'ltr', color: getEfficiencyColor(percent)}}>
                                            {percent > 0 ? "+" : ""}{toPersianDigits(percent)}%
                                        </span>
                                        <button onClick={() => openDetailsModal(`فعالیت: ${act.title}`, act.logs, 'activity')}>بیشتر</button>
                                    </div>
                                );
                            }) : (
                                <div className="empty-state">داده‌ای برای این ماه وجود ندارد</div>
                            )}
                        </section>
                    </div>
                </div>

            </div>
        </div>
    </div>

    {/* ================= MODALS ================= */}

    {/* --- Details Modal --- */}
    {activeModal === 'details' && (
        <div className="edit-modal-overlay" onClick={(e) => { if(e.target === e.currentTarget) closeModal(); }}>
            <div className="edit-modal-content details-modal" style={{ width: '90%', maxWidth: '1200px' }}>
                <div className="edit-modal-header">
                    <h3>{modalTitle}</h3>
                    <button onClick={closeModal} className="close-modal-btn">&times;</button>
                </div>
                
                <div className="edit-modal-body">
                    <div className="task-list-wrapper">
                        <section className="task-list-row-header">
                            <div>عنوان</div>
                            <div>{modalType === 'activity' ? "پروژه" : "فعالیت"}</div>
                            <div>تاریخ</div>
                            <div>مورد انتظار / واقعی</div>
                            <div>بازدهی</div>
                            <div>وضعیت</div>
                        </section>

                        {paginatedModalData.map((task) => {
                            const effPercent = task.efficiency_percentage || 0; 
                            const est = task.estimated_minutes || 0;
                            const act = task.actual_minutes || 0;

                            return (
                                <section key={task.id} className="task-list-row-item">
                                    <div aria-label="عنوان">
                                        <span style={{fontWeight: 'normal'}}>{task.title}</span>
                                    </div>
                                    <div aria-label="پروژه/فعالیت">
                                        <span style={{fontWeight: 'normal'}}>{modalType === 'activity' ? (task.project_title || "-") : (task.task_type_title || "-")}</span>
                                    </div>
                                    <div aria-label="تاریخ">
                                        <span style={{fontWeight: 'normal'}}>{formatJalaliDate(task.start_time)}</span>
                                    </div>
                                    
                                    <div aria-label="زمان" style={{fontSize: '0.9em', color:'#666'}}>
                                       <div>{toPersianDigits(est)}<small style={{fontSize:'0.7rem', opacity:'0.5'}}>min</small></div> / <div>{toPersianDigits(act)}<small style={{fontSize:'0.7rem', opacity:'0.5'}}>min</small></div>
                                    </div>

                                    <div aria-label="بهره‌وری">
                                        {task.status === 'done' ? (
                                            <span style={{
                                                fontWeight: 'normal',
                                                direction:'ltr',
                                                color: getEfficiencyColor(effPercent)
                                            }}>
                                                {effPercent > 0 ? "+" : ""}{toPersianDigits(effPercent)}%
                                            </span>
                                        ) : (
                                            <span style={{color:'#ccc'}}>-</span>
                                        )}
                                    </div>

                                    <div aria-label="وضعیت">
                                        {task.status === 'done' ? <span style={{color: '#10b981'}}>✓ انجام شد</span> :
                                         task.status === 'doing' ? <span style={{color: '#3b82f6'}}>... در حال انجام</span> :
                                         <span style={{color: '#a8a8a8ff'}}>!در انتظار</span>}
                                    </div>
                                </section>
                            );
                        })}
                    </div>

                    {totalPages > 1 && (
                        <div className="paginatioon-wrapper">
                            <button type="button" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>قبلی</button>
                            <span>صفحه <bdi>{toPersianDigits(currentPage)}</bdi> از <ins>{toPersianDigits(totalPages)}</ins></span>
                            <button type="button" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>بعدی</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )}

    {/* ... (Requests modal remains unchanged) ... */}
    {activeModal === 'requests' && (
        <div className="edit-modal-overlay" onClick={(e) => { if(e.target === e.currentTarget) closeModal(); }}>
            <div className="edit-modal-content request-modal" style={{ width: '90%', maxWidth: '1200px' }}>
                <div className="edit-modal-header">
                    <h3>{modalTitle}</h3>
                    <button onClick={closeModal} className="close-modal-btn">&times;</button>
                </div>
                
                <div className="edit-modal-body">
                    <div className="modal-actions">
                        <button 
                            className="save-btn btn-2" 
                            onClick={() => {
                                document.getElementById("addToCall")?.click(); 
                                closeModal();
                            }}
                        >
                            + ثبت درخواست جدید
                        </button>
                    </div>

                    <div className="task-list-wrapper">
                        <section className="task-list-row-header">
                            <div>نوع</div>
                            <div>تاریخ</div>
                            <div>مبلغ</div>
                            <div>وضعیت</div>
                            <div className="flex-2">توضیحات</div>
                        </section>
                        {modalData.length > 0 ? modalData.map((req) => (
                            <section key={req.id} className="task-list-row-item">
                                <div><span style={{fontWeight: 'normal'}}>{req.request_type_title || "درخواست"}</span></div>
                                <div><span style={{fontWeight: 'normal'}}>{formatJalaliDate(req.created_at)}</span></div>
                                <div><span style={{fontWeight: 'normal'}}>{req.amount ? `${toPersianDigits(req.amount)} تومان` : "-"}</span></div>
                                <div>
                                    {req.status === 'approved' ? <span style={{color:'#10b981'}}>تایید شده</span> :
                                     req.status === 'rejected' ? <span style={{color:'#ef4444'}}>رد شده</span> :
                                     <span style={{color:'#f59e0b'}}>در انتظار</span>}
                                </div>
                                <div className="flex-2"><span className="description-text">{req.description || "-"}</span></div>
                            </section>
                        )) : <p className="empty-state">درخواستی ثبت نشده است</p>}
                    </div>
                </div>
            </div>
        </div>
    )}

    </>
  );
}